import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';
import { characterMap } from '../assets/characters';
import { usePageTitle } from '../hooks/usePageTitle';
import { SEOMeta } from '../templates/SEOMeta';
import { getOrCreateSessionToken } from '../utils/sessionManager';
import { logger } from '../utils/logger';
import { useLocalizedNavigate } from '../i18n/hooks';
import RecommendedSection from './RecommendedSection';

interface CreateRoomData {
  creatorName: string;
  password: string;
  maxPlayers: number;
  minPlayers: number;
}

interface RateLimitError {
  message: string;
  activeRoomCodes?: string[];
}

const CreateRoom: React.FC = () => {
  const { t } = useTranslation();
  usePageTitle(t('create.pageTitle'));
  const navigate = useLocalizedNavigate();
  const [formData, setFormData] = useState<CreateRoomData>({
    creatorName: localStorage.getItem('playerName') || '',
    password: '',
    maxPlayers: 10,
    minPlayers: 3
  });
  const [selectedCharacter, setSelectedCharacter] = useState<string>(localStorage.getItem('playerCharacter') || '');
  const [usePassword, setUsePassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeRoomCodes, setActiveRoomCodes] = useState<string[]>([]);

  const [publicRooms, setPublicRooms] = useState<any[]>([]);
  const [loadingPublicRooms, setLoadingPublicRooms] = useState(false);
  const [roomsError, setRoomsError] = useState('');
  const [joiningPublicRoomCode, setJoiningPublicRoomCode] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'maxPlayers' || name === 'minPlayers' ? parseInt(value) || 3 : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const requestData = {
        creatorName: formData.creatorName,
        password: usePassword ? formData.password : '',
        maxPlayers: formData.maxPlayers,
        minPlayers: formData.minPlayers
      };

      const response = await apiClient.post('/api/rooms', requestData);
      const roomCode = response.data.code;
      
      // Generar sessionToken usando el nuevo sistema persistente
      const sessionToken = getOrCreateSessionToken(roomCode, formData.creatorName);
      
      // Guardar información del jugador (ya no usar sessionStorage)
      localStorage.setItem('playerName', formData.creatorName);
      if (selectedCharacter) localStorage.setItem('playerCharacter', selectedCharacter);
      
      // Unirse automáticamente a la sala creada
      await apiClient.post(`/api/rooms/${roomCode}/join`, {
        roomCode,
        playerName: formData.creatorName,
        password: requestData.password,
        character: selectedCharacter,
        sessionId: sessionToken // Usar sessionToken en lugar de sessionId antiguo
      });

      // Redirigir a la sala
      navigate(`/room/${roomCode}`);
    } catch (err: any) {
      logger.error('Error creating room:', err);
      
      // Capturar códigos de salas activas si vienen en la respuesta
      if (err.response?.data?.activeRoomCodes) {
        setActiveRoomCodes(err.response.data.activeRoomCodes);
      } else {
        setActiveRoomCodes([]);
      }
      
      setError(err.response?.data?.message || t('create.errors.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Obtener lista de salas públicas activas (sin contraseña)
  const fetchPublicRooms = async () => {
    setLoadingPublicRooms(true);
    setRoomsError('');
    try {
      const resp = await apiClient.get('/api/rooms/public');
      const rooms: any[] = Array.isArray(resp.data) ? resp.data : [];
      setPublicRooms(rooms);
    } catch (err: any) {
      setRoomsError(t('join.errors.publicRoomsFailed'));
      setPublicRooms([]);
    } finally {
      setLoadingPublicRooms(false);
    }
  };

  useEffect(() => {
    fetchPublicRooms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJoinPublicRoom = async (roomCode: string) => {
    const playerName = (formData.creatorName || '').trim();
    if (playerName.length < 2) {
      setError(t('create.errors.nameRequiredToJoinPublic'));
      return;
    }

    setJoiningPublicRoomCode(roomCode);
    setError('');

    try {
      const sessionToken = getOrCreateSessionToken(roomCode, playerName);

      await apiClient.post(`/api/rooms/${roomCode}/join`, {
        roomCode,
        playerName,
        password: '',
        character: selectedCharacter,
        sessionId: sessionToken
      });

      localStorage.setItem('playerName', playerName);
      if (selectedCharacter) localStorage.setItem('playerCharacter', selectedCharacter);

      navigate(`/room/${roomCode}`);
    } catch (err: any) {
      logger.error('Error joining public room:', err);
      if (err.response?.status === 403) {
        setError(t('join.errors.wrongPassword'));
      } else if (err.response?.status === 404) {
        setError(t('join.errors.roomNotFound'));
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(t('join.errors.joinFailed'));
      }
    } finally {
      setJoiningPublicRoomCode(null);
    }
  };

  return (
    <>
      <SEOMeta
        title={t('create.seo.title')}
        description={t('create.seo.description')}
        canonical="https://el-impostor.com/create"
      />
      <div className="min-h-screen flex items-center justify-center p-4 bg-blue-50 dark:bg-gray-950">
      <div className="max-w-md w-full">
        <RecommendedSection position="top" />
        <div className="text-center mb-6">
          <button 
            onClick={() => navigate('/')}
            className="text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200 mb-4 inline-flex items-center text-sm"
          >
            {t('create.backHome')}
          </button>
          <h1 className="text-3xl font-bold text-primary-800 mb-2">
            {t('create.header.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            {t('create.header.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 px-4 py-3 rounded-lg text-sm space-y-2">
              <div>{error}</div>
              {activeRoomCodes.length > 0 && (
                <div className="mt-3 pt-3 border-t border-red-300 dark:border-red-800">
                  <p className="font-semibold mb-2">{t('create.activeRooms.title')}</p>
                  <div className="space-y-2">
                    {activeRoomCodes.map((code) => (
                      <div key={code} className="flex items-center justify-between bg-red-100 dark:bg-red-900/30 p-2 rounded">
                        <span className="font-mono font-bold">{code}</span>
                        <button
                          type="button"
                          onClick={() => navigate(`/room/${code}`)}
                          className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded transition-colors"
                        >
                          {t('create.activeRooms.goToRoom')}
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs mt-2 text-red-600 dark:text-red-300">
                    {t('create.activeRooms.hint')}
                  </p>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              {t('create.form.nameLabel')}
            </label>
            <input
              type="text"
              name="creatorName"
              value={formData.creatorName}
              onChange={handleInputChange}
              className="input-field"
              placeholder={t('create.form.namePlaceholder')}
              minLength={2}
              maxLength={20}
              required
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('create.form.passwordToggleLabel')}
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('create.form.passwordToggleHelp')}</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={usePassword}
                onChange={(e) => setUsePassword(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:after:bg-gray-200 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>

          {usePassword && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                {t('create.form.passwordLabel')}
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="input-field"
                placeholder={t('create.form.passwordPlaceholder')}
                maxLength={50}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                {t('create.form.minPlayersLabel')}
              </label>
              <input
                type="number"
                name="minPlayers"
                value={formData.minPlayers}
                onChange={handleInputChange}
                className="input-field"
                min={3}
                max={formData.maxPlayers}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                {t('create.form.maxPlayersLabel')}
              </label>
              <input
                type="number"
                name="maxPlayers"
                value={formData.maxPlayers}
                onChange={handleInputChange}
                className="input-field"
                min={formData.minPlayers}
                max={20}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              {t('create.form.characterLabel')}
            </label>
            <div className="grid grid-cols-5 gap-2 mb-2">
              {Object.keys(characterMap).map((id) => (
                <label key={id} className={`p-1 rounded cursor-pointer border ${selectedCharacter === id ? 'ring-2 ring-primary-500' : 'border-transparent'}`}>
                  <input type="radio" name="character" value={id} checked={selectedCharacter === id} onChange={() => setSelectedCharacter(id)} className="sr-only" />
                  <img src={characterMap[id]} alt={id} className="w-12 h-12 object-cover rounded" loading="lazy" />
                </label>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('create.submit.creating')}
              </span>
            ) : (
              t('create.submit.create')
            )}
          </button>
        </form>

        <div className="text-center mt-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('create.footer.autoCloseNote')}
          </p>
        </div>

        {/* Salas públicas disponibles */}
        <div className="mt-6 card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-primary-800 dark:text-primary-200 flex items-center gap-2">
              {t('join.publicRooms.title')}
              {publicRooms.length > 0 && (
                <span className="text-xs font-normal bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200 px-2 py-1 rounded-full">
                  {publicRooms.length}
                </span>
              )}
            </h2>
            <button
              onClick={fetchPublicRooms}
              disabled={loadingPublicRooms}
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-200 dark:hover:text-primary-100 disabled:opacity-50 flex items-center gap-1"
            >
              {loadingPublicRooms ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600"></div>
                  {t('join.publicRooms.refreshing')}
                </>
              ) : (
                <>{t('join.publicRooms.refresh')}</>
              )}
            </button>
          </div>

          {roomsError && (
            <div className="text-sm text-red-600 dark:text-red-200 mb-3 p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
              {roomsError}
            </div>
          )}

          {loadingPublicRooms && publicRooms.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
              <p className="text-sm">{t('join.publicRooms.loading')}</p>
            </div>
          ) : publicRooms.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <div className="text-4xl mb-2">🏜️</div>
              <p className="text-sm">{t('join.publicRooms.emptyTitle')}</p>
              <p className="text-xs mt-1">{t('join.publicRooms.emptySubtitle')}</p>
            </div>
          ) : (
            <div className="grid gap-3 max-h-80 overflow-y-auto">
              {publicRooms.map((r) => {
                const code = (r.code || r.roomCode)?.toString();
                if (!code) return null;
                const isFull = r.playerCount >= r.maxPlayers;
                const isJoiningThis = joiningPublicRoomCode === code;

                return (
                  <div
                    key={code}
                    className="p-4 border-2 rounded-lg flex items-center justify-between bg-gradient-to-r from-white to-primary-50 dark:from-gray-900 dark:to-gray-950 hover:shadow-md transition-all duration-200 hover:border-primary-300 dark:border-gray-800"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xl font-bold tracking-widest text-primary-800 dark:text-primary-200">
                          {code}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-300">
                        👤 {r.creatorName || t('join.publicRooms.hostFallback')} • {t('join.roomInfo.playersCount', { current: r.playerCount, max: r.maxPlayers })}
                      </div>
                    </div>

                    <div className="ml-4">
                      <button
                        className="px-4 py-2 text-sm font-semibold border-2 border-primary-600 rounded-lg text-primary-600 hover:bg-primary-600 hover:text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleJoinPublicRoom(code)}
                        disabled={isFull || isJoiningThis}
                      >
                        {isFull ? t('join.publicRooms.full') : isJoiningThis ? t('join.submit.joining') : t('join.publicRooms.joinFromList')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">
            {t('join.publicRooms.hint')}
          </div>
        </div>
        <RecommendedSection position="bottom" />
      </div>
    </div>
    </>
  );
};

export default CreateRoom;

